import { createAsyncThunk, ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { RootState } from 'store';
import HttpError from '@common/interfaces/api/http-error';
import ValidationResult from '@common/interfaces/api/validation-result';
import {
  returnAxiosOrGeneralFetchError,
  returnAxiosValidationOrGeneralFetchError,
} from '../../services/data/axios-base';
import localRobotDataService from 'services/data/local-robot-data-service';
import { selectSelectedRobotId } from 'store/robots/robots-slice-selectors';
import FloorData, { FloorApiData } from '@common/interfaces/robot/rooms/floor-data';
import RoomData, {
  mapPartialApiToRoom,
  mapPartialRoomToApi,
  RoomApiData,
} from '@common/interfaces/robot/rooms/room-data';
import CleaningZone, {
  isCleaningZone,
  mapCleaningZoneApiToCleaningZone,
  mapCleaningZoneToApi,
  mapPartialCleaningZoneToApi,
} from '@common/interfaces/robot/rooms/cleaning-zone';
import RedZone, {
  mapPartialRedZoneToApi,
  mapRedZoneApiToRedZone,
  mapRedZoneToApi,
  newRedZone,
} from '@common/interfaces/robot/rooms/red-zone';
import RuleZone, {
  isRuleZone,
  mapPartialRuleZoneToApi,
  mapRuleZoneApiToRuleZone,
  mapRuleZoneToApi,
} from '@common/interfaces/robot/rooms/rule-zone';
import Landmark, {
  mapLandmarkApiToLandmark,
  mapLandmarkToApi,
  mapPartialLandmarkToApi,
} from '@common/interfaces/robot/rooms/landmark';
import {
  WaypointEdge,
  WaypointNode,
  WaypointSetValidationResult,
  createEdge,
  findNearestNodeConnection,
  mapNodeApiToNode,
  mapNodeToNodeApi,
  validateWayPoints,
} from '@common/interfaces/robot/rooms/waypoints';
import Mission, {
  mapMissionApiDataToMission,
  mapMissionToApi,
  mapPartialMissionToApi,
} from '@common/interfaces/robot/missions/mission';
import ScheduleEntry, {
  mapPartialScheduleEntryToApi,
  mapScheduleEntryApiToScheduleEntry,
  mapScheduleEntryToScheduleEntryApi,
} from '@common/interfaces/robot/scheduling/schedule-entry';
import RoomLimitZone, {
  mapLimitZoneApiToLimitZone,
  mapPartialLimitZoneToApi,
} from '@common/interfaces/robot/rooms/room-limit-zone';
import { popUndoRedoAction } from './local-robot-slice';
import MapHistoryEntry, {
  isHistoryCleaningZone,
  isHistoryLandmark,
  isHistoryRedZone,
  isHistoryRuleZone,
  isHistoryTeachAndRepeatZonePath,
  isHistoryWaypointSet,
  MapHistoryAction,
  MapHistoryType,
} from '@common/interfaces/robot/zone-planner/map-history';
import { selectZoneById, selectRepeatPathByZoneId, getNewName } from './state-helpers';
import DeepPartial, { mergeDeep } from '@common/utils/deep-partial';
import TeachAndRepeatZonePath, {
  mapTeachAndRepeatZonePathToApi,
  mapTeachAndRepeatZonePathApiToTeachAndRepeatZonePath,
  mapPartialTeachAndRepeatZonePathToApi,
  TeachAndRepeatZonePathApi,
} from '@common/interfaces/robot/rooms/teach-and-repeat-zone-path';
import ZonePointType from '@common/enums/robot/rooms/zone-point-type';
import cloneDeep from 'lodash.clonedeep';
import {
  validateCleaningZone,
  validateRedZone,
  validateRuleZone,
} from '@common/validation/zone-validation';
import { validateRepeatPath } from '@common/validation/repeat-path-validation';
import { validateGate, validateRoomGates } from '@common/validation/gate-validation';
import {
  calculateGatePoints,
  copyGate,
  GateSet,
  mapGateSetApiToGateSet,
  mapGateSetToApi,
} from '@common/interfaces/robot/rooms/gate';
import Point, { setPointInRoom } from '@common/interfaces/robot/rooms/point';
import i18n from '@common/i18n';
import PaginationWrapper from 'models/misc/api/pagination-wrapper';
import Error from '@common/interfaces/misc/errors';
import { trackEvent } from 'services/analytics';
import { AnalyticsEvent } from 'services/analytics-events';

export const getFloorsByRobotId = createAsyncThunk<
  FloorApiData[],
  string,
  { rejectValue: HttpError }
>('local-robot/floors/list', async (id, { rejectWithValue }) => {
  try {
    return await localRobotDataService.getFloorsByRobotId(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const patchFloor = createAsyncThunk<
  Partial<FloorData>,
  { id: string; data: Partial<FloorData> },
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/floors/patch', async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.patchFloorByRobotId(robotId, payload.id, payload.data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchRoomById = createAsyncThunk<
  Partial<RoomData>,
  { id: string; data: DeepPartial<RoomData> },
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/rooms/patch', async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const outgoingApiData = mapPartialRoomToApi(payload.data);
  try {
    const incomingApiData = await localRobotDataService.patchRoomById(
      robotId,
      payload.id,
      outgoingApiData,
    );
    return mapPartialApiToRoom(incomingApiData);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const deleteRobotMissionById = createAsyncThunk<
  string,
  string,
  { state: RootState; rejectValue: HttpError }
>('local-robot/missions/delete', async (missionId, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.deleteRobotMissionById(robotId, missionId);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const postNewRobotMission = createAsyncThunk<
  Mission,
  Mission,
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/missions/post', async (mission, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const outgoingApiData = mapMissionToApi(mission);
  try {
    const incomingApiData = await localRobotDataService.postNewRobotMission(
      robotId,
      outgoingApiData,
    );
    return mapMissionApiDataToMission(incomingApiData);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchRobotMission = createAsyncThunk<
  Mission,
  { id: string; data: Partial<Mission> },
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/missions/patch', async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const outgoingApiData = mapPartialMissionToApi(payload.data);
  try {
    const incomingApiData = await localRobotDataService.patchRobotMission(
      robotId,
      payload.id,
      outgoingApiData,
    );
    return mapMissionApiDataToMission(incomingApiData);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const getGlobalMissions = createAsyncThunk<
  Mission[],
  void,
  { state: RootState; rejectValue: HttpError }
>('local-robot/missions/get', async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    const apiData = await localRobotDataService.getGlobalMissions(robotId);
    return apiData.map(mapMissionApiDataToMission);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const deleteRobotScheduleById = createAsyncThunk<
  string,
  string,
  { state: RootState; rejectValue: HttpError }
>('local-robot/schedules/delete', async (scheduleId, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.deleteRobotScheduleById(robotId, scheduleId);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const postNewRobotSchedule = createAsyncThunk<
  ScheduleEntry,
  ScheduleEntry,
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/schedules/post', async (scheduleEntry, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const outgoingApiData = mapScheduleEntryToScheduleEntryApi(scheduleEntry);
  try {
    const incomingApiData = await localRobotDataService.postNewRobotSchedule(
      robotId,
      outgoingApiData,
    );
    return mapScheduleEntryApiToScheduleEntry(incomingApiData);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchRobotSchedule = createAsyncThunk<
  ScheduleEntry,
  { id: string; data: Partial<ScheduleEntry> },
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/schedules/patch', async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);
  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const outgoing = mapPartialScheduleEntryToApi(payload.data);
  try {
    const incoming = await localRobotDataService.patchRobotSchedule(robotId, payload.id, outgoing);
    return mapScheduleEntryApiToScheduleEntry(incoming);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchEnableAllSchedules = createAsyncThunk<
  { enabled: boolean; floor_id: string },
  string,
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/schedules/enable-all', async (floorId, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);
  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    const enabled = await localRobotDataService.enableAllSchedules(robotId, floorId);
    return enabled;
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchDisableAllSchedules = createAsyncThunk<
  { enabled: boolean; floor_id: string },
  string,
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/schedules/disable-all', async (floorId, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);
  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    const disabled = await localRobotDataService.disableAllSchedules(robotId, floorId);
    return disabled;
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const postNewCleaningZone = createAsyncThunk<
  { cleaningZone: CleaningZone; teachAndRepeatPath: TeachAndRepeatZonePath | null },
  { cleaningZone: CleaningZone; teachAndRepeatPath?: TeachAndRepeatZonePath },
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>(
  'local-robot/cleaning-zones/post',
  async ({ cleaningZone, teachAndRepeatPath }, { getState, rejectWithValue, dispatch }) => {
    const state = getState();
    const robotId = selectSelectedRobotId(state);

    if (robotId == null)
      return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

    const outgoingApiData = {
      ...mapCleaningZoneToApi(cleaningZone),
      ...(teachAndRepeatPath != null && {
        teachAndRepeatPath: mapTeachAndRepeatZonePathToApi(teachAndRepeatPath),
      }),
    };
    try {
      const incomingApiData = await localRobotDataService.postNewCleaningZone(
        robotId,
        outgoingApiData,
      );
      const incomingCleaningZone = mapCleaningZoneApiToCleaningZone(incomingApiData);
      const incomingRepeatPath =
        incomingApiData.teachAndRepeatPath != null
          ? mapTeachAndRepeatZonePathApiToTeachAndRepeatZonePath(incomingApiData.teachAndRepeatPath)
          : null;
      return {
        cleaningZone: incomingCleaningZone,
        teachAndRepeatPath: incomingRepeatPath,
      };
    } catch (error) {
      return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
    }
  },
);

export const patchCleaningZone = createAsyncThunk<
  { cleaningZone: CleaningZone; teachAndRepeatPath: TeachAndRepeatZonePath | null },
  {
    id: string;
    data: {
      cleaningZone?: Partial<CleaningZone>;
      teachAndRepeatPath?: Partial<TeachAndRepeatZonePath>;
    };
  },
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/cleaning-zones/patch', async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const cleaningZone = state.localRobot.cleaningZones.data.find((c) => c.id === payload.id);
  if (cleaningZone == null) {
    return rejectWithValue({ code: '404', message: 'No cleaning zone id found', value: null });
  }

  const patchedCleaningZone =
    payload.data.cleaningZone != null
      ? mergeDeep(cleaningZone, payload.data.cleaningZone)
      : cleaningZone;
  const validationResults = validateCleaningZone(patchedCleaningZone);
  const stateTeachAndRepeatPath = state.localRobot.teachRepeatPaths.data.find(
    (trp) => trp.zoneId === payload.id,
  );
  let patchedRepeatPath: TeachAndRepeatZonePath | null = null;

  if (payload.data.teachAndRepeatPath != null) {
    if (stateTeachAndRepeatPath == null)
      return rejectWithValue({
        code: '400',
        message: 'Cannot patch Teach And Repeat Zone that does not have a path in React State',
        value: null,
      });
    patchedRepeatPath =
      payload.data.teachAndRepeatPath != null
        ? mergeDeep(stateTeachAndRepeatPath, payload.data.teachAndRepeatPath)
        : stateTeachAndRepeatPath;
    validationResults.push(...validateRepeatPath(patchedRepeatPath, patchedCleaningZone));
  }

  if (validationResults.length > 0) {
    return rejectWithValue(validationResults);
  }

  const outgoingApiData = {
    ...(payload.data.cleaningZone != null &&
      mapPartialCleaningZoneToApi(payload.data.cleaningZone)),
    ...(payload.data.teachAndRepeatPath != null && {
      teachAndRepeatPath: mapPartialTeachAndRepeatZonePathToApi(payload.data.teachAndRepeatPath),
    }),
  };

  try {
    const incomingApiData = await localRobotDataService.patchCleaningZone(
      robotId,
      payload.id,
      outgoingApiData,
    );
    const incomingCleaningZone = mapCleaningZoneApiToCleaningZone(incomingApiData);
    const incomingRepeatPath =
      incomingApiData?.teachAndRepeatPath != null
        ? mapTeachAndRepeatZonePathApiToTeachAndRepeatZonePath(incomingApiData.teachAndRepeatPath)
        : null;
    return { cleaningZone: incomingCleaningZone, teachAndRepeatPath: incomingRepeatPath };
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const deleteCleaningZoneById = createAsyncThunk<
  RoomApiData,
  string,
  { state: RootState; rejectValue: HttpError }
>('local-robot/cleaning-zones/delete', async (zoneId, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.deleteRobotCleaningZoneById(robotId, zoneId);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

// export const postNewTeachAndRepeatZonePath = createAsyncThunk<
//   TeachAndRepeatZonePath,
//   TeachAndRepeatZonePath,
//   {state : RootState; rejectValue: ValidationResult[] | HttpError }
//   >("local-robot/repeat-paths/post", async (teachAndRepeatZonePath, { getState, rejectWithValue, dispatch }) => {
//     const state = getState();
//     const robotId = selectSelectedRobotId(state);

//     if (robotId == null) return rejectWithValue({ code: "400", message: "No robot id found", value: null });

//     const outgoingApiData = mapTeachAndRepeatZonePathToApi(teachAndRepeatZonePath);
//     try{
//       const incomingApiData = await localRobotDataService.postNewTeachAndRepeatZonePath(robotId, outgoingApiData);
//       return mapTeachAndRepeatZonePathApiToTeachAndRepeatZonePath(incomingApiData);
//     } catch (error) {
//       return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
//     }
//   }
// );

// export const deleteRobotTeachAndRepeatZonePathById = createAsyncThunk<
// RoomApiData,
// string,
// {state: RootState; rejectValue: HttpError }
// > ("local-robot/repeat-paths/delete", async (trPathId, {getState, rejectWithValue}) =>{
//   const state = getState();
//   const robotId = selectSelectedRobotId(state);

//   if (robotId == null) return rejectWithValue ({code: "400", message: "No robot id found", value: null});

//   try{
//     return await localRobotDataService.deleteRobotTeachAndRepeatZonePathById(robotId, trPathId);
//   } catch (error){
//     return rejectWithValue(returnAxiosOrGeneralFetchError(error));
//   }
// });

export const postNewRedZone = createAsyncThunk<
  RedZone,
  RedZone,
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/red-zones/post', async (redZone, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const redZoneRoom = state.localRobot.rooms.data.find((r) => r.id === redZone.room);
  if (redZoneRoom && validateRedZone(redZone, redZoneRoom).length > 0) {
    return rejectWithValue(validateRedZone(redZone, redZoneRoom));
  }

  const outgoingApiData = mapRedZoneToApi(redZone);
  try {
    const incomingApiData = await localRobotDataService.postNewRedZone(robotId, outgoingApiData);
    return mapRedZoneApiToRedZone(incomingApiData);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchRedZone = createAsyncThunk<
  RedZone,
  { id: string; data: Partial<RedZone> },
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/red-zones/patch', async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const redZone = state.localRobot.redZones.data.find((c) => c.id === payload.id);
  if (redZone == null) {
    return rejectWithValue({ code: '404', message: 'No red zone id found', value: null });
  }
  const patchedRedZone = { ...redZone, ...payload.data } as RedZone;
  const redZoneRoom = state.localRobot.rooms.data.find((r) => r.id === patchedRedZone.room);
  let gateValidationResults: ValidationResult[] = [];

  if (patchedRedZone.gateId !== '') {
    const gate = state.localRobot.gates.data.find((g) => g.id === patchedRedZone.gateId);
    const nodes = state.localRobot.waypoints.data.nodes.filter(
      (wpn) => wpn.room === patchedRedZone.room,
    );
    const edges = state.localRobot.waypoints.data.edges.filter(
      (wpe) => wpe.room === patchedRedZone.room,
    );

    if (gate != null) {
      gateValidationResults = validateGate(gate, patchedRedZone, state.localRobot.gates.data, {
        nodes,
        edges,
      });
    }
  }

  if (redZoneRoom) {
    const validationErrors = [
      ...validateRedZone(patchedRedZone, redZoneRoom),
      ...gateValidationResults,
    ];
    if (validationErrors.length > 0) {
      return rejectWithValue(validationErrors);
    }
  }

  const outgoingApiData = mapPartialRedZoneToApi(payload.data);

  try {
    const incomingApiData = await localRobotDataService.patchRedZone(
      robotId,
      payload.id,
      outgoingApiData,
    );
    return mapRedZoneApiToRedZone(incomingApiData);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const deleteRedZoneById = createAsyncThunk<
  string,
  string,
  { state: RootState; rejectValue: HttpError }
>('local-robot/red-zones/delete', async (zoneId, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.deleteRobotRedZoneById(robotId, zoneId);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const patchLimitZone = createAsyncThunk<
  RoomLimitZone,
  { id: string; data: Partial<RoomLimitZone> },
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/limit-zones/patch', async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const outgoingApiData = mapPartialLimitZoneToApi(payload.data);
  try {
    const incomingApiData = await localRobotDataService.patchLimitZone(
      robotId,
      payload.id,
      outgoingApiData,
    );
    return mapLimitZoneApiToLimitZone(incomingApiData);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const postNewRuleZone = createAsyncThunk<
  RuleZone,
  RuleZone,
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/rule-zones/post', async (ruleZone, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const ruleZonesInRoom = state.localRobot.ruleZones.data.filter((rz) => rz.room === ruleZone.room);

  if (ruleZonesInRoom && validateRuleZone(ruleZone, ruleZonesInRoom).length > 0) {
    return rejectWithValue(validateRuleZone(ruleZone, ruleZonesInRoom));
  }

  const outgoingApiData = mapRuleZoneToApi(ruleZone);
  try {
    const incomingApiData = await localRobotDataService.postNewRuleZone(robotId, outgoingApiData);
    return mapRuleZoneApiToRuleZone(incomingApiData);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchRuleZone = createAsyncThunk<
  RuleZone,
  { id: string; data: Partial<RuleZone> },
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/rule-zones/patch', async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const ruleZone = state.localRobot.ruleZones.data.find((rz) => rz.id === payload.id);
  if (ruleZone == null) {
    return rejectWithValue({ code: '404', message: 'No rule zone id found', value: null });
  }
  const patchedRuleZone = { ...ruleZone, ...payload.data } as RuleZone;
  const ruleZonesInRoom = state.localRobot.ruleZones.data.filter(
    (rz) => rz.room === patchedRuleZone.room,
  );

  if (ruleZonesInRoom && validateRuleZone(patchedRuleZone, ruleZonesInRoom).length > 0) {
    return rejectWithValue(validateRuleZone(patchedRuleZone, ruleZonesInRoom));
  }

  const outgoingApiData = mapPartialRuleZoneToApi(payload.data);

  try {
    const incomingApiData = await localRobotDataService.patchRuleZone(
      robotId,
      payload.id,
      outgoingApiData,
    );
    return mapRuleZoneApiToRuleZone(incomingApiData);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const deleteRuleZoneById = createAsyncThunk<
  string,
  string,
  { state: RootState; rejectValue: HttpError }
>('local-robot/rule-zones/delete', async (zoneId, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.deleteRobotRuleZoneById(robotId, zoneId);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const postNewLandmark = createAsyncThunk<
  Landmark,
  Landmark,
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/landmarks/post', async (landmark, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const outgoingApiData = mapLandmarkToApi(landmark);
  try {
    const incomingApiData = await localRobotDataService.postNewLandmark(robotId, outgoingApiData);
    return mapLandmarkApiToLandmark(incomingApiData);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchLandmark = createAsyncThunk<
  Landmark,
  { id: string; data: Partial<Landmark> },
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('local-robot/landmarks/patch', async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const outgoingApiData = mapPartialLandmarkToApi(payload.data);
  try {
    const incomingApiData = await localRobotDataService.patchLandmark(
      robotId,
      payload.id,
      outgoingApiData,
    );
    return mapLandmarkApiToLandmark(incomingApiData);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const deleteLandmarkById = createAsyncThunk<
  RoomApiData,
  string,
  { state: RootState; rejectValue: HttpError }
>('local-robot/landmarks/delete', async (landmarkId, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  try {
    return await localRobotDataService.deleteRobotLandmarkById(robotId, landmarkId);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const upsertWaypointSet = createAsyncThunk<
  { edges: WaypointEdge[]; nodes: WaypointNode[] },
  { room: string; nodes: WaypointNode[]; edges: WaypointEdge[] },
  { state: RootState; rejectValue: ValidationResult[] | HttpError | WaypointSetValidationResult }
>('local-robot/waypoints/upsert', async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const nodesInRoom = payload.nodes.filter((n) => n.room === payload.room);
  const edgesInRoom = payload.edges.filter((e) => e.room === payload.room);
  const gates = state.localRobot.gates.data.filter((g) => g.room === payload.room);
  const redZonesInRoom = state.localRobot.redZones.data.filter((rz) => rz.room === payload.room);

  const roomGatesValidationResults: ValidationResult[] = validateRoomGates(
    payload.room,
    gates,
    redZonesInRoom,
    { nodes: nodesInRoom, edges: edgesInRoom },
  );
  const wayPointsValidationResult = validateWayPoints({ nodes: nodesInRoom, edges: edgesInRoom });
  const validationResults = [...roomGatesValidationResults, wayPointsValidationResult].filter(
    (vr) => !vr.isValid,
  );

  if (validationResults.length > 0) {
    return rejectWithValue({
      room: payload.room,
      waypointSet: { nodes: nodesInRoom, edges: edgesInRoom },
      validationErrors: validationResults,
    });
  }

  const outgoingApiData = {
    room: payload.room,
    waypointEdges: edgesInRoom,
    waypointNodes: nodesInRoom.map((node) => mapNodeToNodeApi(node)),
  };
  try {
    const incomingApiData = await localRobotDataService.upsertWaypointSet(robotId, outgoingApiData);
    return {
      edges: incomingApiData.waypointEdges,
      nodes: incomingApiData.waypointNodes.map((n) => mapNodeApiToNode(n)),
    };
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchGateById = createAsyncThunk<
  GateSet,
  { id: string; data: DeepPartial<GateSet> },
  {
    state: RootState;
    rejectValue: ValidationResult[] | HttpError;
  }
>('gates/patch', async (payload, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const updatedGate = state.localRobot.gates.data.find((g) => g.id === payload.id);

  const edges = state.localRobot.waypoints.data.edges.filter(
    (wpe) => wpe.room === updatedGate?.room,
  );
  const nodes = state.localRobot.waypoints.data.nodes.filter(
    (wpn) => wpn.room === updatedGate?.room,
  );
  const zone = state.localRobot.redZones.data.find((rz) => rz.gateId === updatedGate?.id);

  if (updatedGate == null || zone == null) {
    return rejectWithValue({
      code: '400',
      message: 'Cannot patch a gate that is not in the React state',
      value: null,
    });
  }

  const patchData = mergeDeep(
    { gate: updatedGate, redZone: zone, waypointSet: { edges, nodes } } as GateSet,
    payload.data,
  );

  const validationErrors = validateGate(
    patchData.gate,
    patchData.redZone,
    state.localRobot.gates.data,
    { nodes, edges },
  );
  if (validationErrors.length > 0) {
    return rejectWithValue(validationErrors);
  }

  const outgoingApiData = mapGateSetToApi(patchData);

  try {
    const incomingApiData = await localRobotDataService.patchRobotGateById(
      robotId,
      outgoingApiData,
    );
    return mapGateSetApiToGateSet(incomingApiData);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const postNewGate = createAsyncThunk<
  GateSet,
  void,
  {
    state: RootState;
    rejectValue: ValidationResult[] | HttpError;
  }
>('gates/post', async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const existingGate = state.localRobot.gates.data.find(
    (g) => g.id === state.localRobot.gates.selectedId,
  );
  const roomData = state.localRobot.rooms.data.find((r) => r.id === existingGate?.room);

  if (existingGate == null || roomData == null) {
    return rejectWithValue({
      code: '400',
      message: 'Cannot create a gate that is not in the React state',
      value: null,
    });
  }

  const selectedGate = copyGate(existingGate);

  if (
    Math.sqrt(
      (selectedGate.aPost.coordinateX - selectedGate.bPost.coordinateX) ** 2 +
        (selectedGate.aPost.coordinateY - selectedGate.bPost.coordinateY) ** 2,
    ) < 1.2
  ) {
    selectedGate.aPost = {
      ...selectedGate.aPost,
      ...setPointInRoom(
        {
          coordinateX: selectedGate.aPost.coordinateX - 1,
          coordinateY: selectedGate.aPost.coordinateY,
        } as Point,
        roomData,
      ),
    };
    selectedGate.bPost = {
      ...selectedGate.bPost,
      ...setPointInRoom(
        {
          coordinateX: selectedGate.aPost.coordinateX + 1,
          coordinateY: selectedGate.aPost.coordinateY,
        } as Point,
        roomData,
      ),
    };
  }
  const gatePoints = calculateGatePoints(selectedGate.aPost, selectedGate.bPost, roomData);

  const wayPoints = [
    {
      id: selectedGate.aWaypoint,
      gateId: selectedGate.id,
      room: roomData.id,
      point: { ...gatePoints.aWayPoint, coordinateTheta: 0 },
    } as WaypointNode,
    {
      id: selectedGate.bWaypoint,
      gateId: selectedGate.id,
      room: roomData.id,
      point: { ...gatePoints.bWayPoint, coordinateTheta: 0 },
    } as WaypointNode,
    {
      id: selectedGate.mWaypoint,
      gateId: selectedGate.id,
      room: roomData.id,
      point: { ...gatePoints.mWayPoint, coordinateTheta: 0 },
    } as WaypointNode,
  ];

  const edges = [
    createEdge(state.localRobot.waypoints.data.edges, wayPoints[0], wayPoints[2], roomData.id)!,
    createEdge(state.localRobot.waypoints.data.edges, wayPoints[1], wayPoints[2], roomData.id)!,
  ];

  const mWaypointNodes = state.localRobot.gates.data.map((g) => g.mWaypoint);

  const gateConnectionEdge = findNearestNodeConnection(
    wayPoints[0],
    wayPoints[1],
    state.localRobot.waypoints.data.nodes.filter(
      (n) =>
        n.id !== selectedGate.aWaypoint &&
        n.id !== selectedGate.bWaypoint &&
        n.id !== selectedGate.mWaypoint &&
        !mWaypointNodes.includes(n.id) && // do not connect to a mWaypoint, those are inside gates/redzones
        n.room === roomData.id,
    ),
  );

  if (gateConnectionEdge != null) {
    edges.push(
      createEdge(
        state.localRobot.waypoints.data.edges,
        gateConnectionEdge.from,
        gateConnectionEdge.to,
        roomData.id,
      )!,
    );
  }

  const newZoneName = getNewName(
    i18n.t('red-zone'),
    state.localRobot.redZones.data.filter((c) => c.name != null).map((c) => c.name!),
  );

  const newZone = newRedZone(newZoneName, roomData.id, false, selectedGate.id);
  newZone.id = selectedGate.redzone;
  newZone.points = [
    {
      orderIndex: 0,
      point: { ...gatePoints.redZonePoint1, type: ZonePointType.RedZoneCorner, id: uuidv4() },
    },
    {
      orderIndex: 1,
      point: { ...gatePoints.redZonePoint2, type: ZonePointType.RedZoneCorner, id: uuidv4() },
    },
    {
      orderIndex: 2,
      point: { ...gatePoints.redZonePoint3, type: ZonePointType.RedZoneCorner, id: uuidv4() },
    },
    {
      orderIndex: 3,
      point: { ...gatePoints.redZonePoint4, type: ZonePointType.RedZoneCorner, id: uuidv4() },
    },
  ];

  const outgoingApiData = mapGateSetToApi({
    gate: selectedGate,
    redZone: newZone,
    waypointSet: {
      edges: [
        ...edges,
        ...state.localRobot.waypoints.data.edges.filter((e) => e.room === roomData.id),
      ],
      nodes: [
        ...wayPoints,
        ...state.localRobot.waypoints.data.nodes.filter((n) => n.room === roomData.id),
      ],
    },
  });
  try {
    const incomingApiData = await localRobotDataService.saveRobotGateById(robotId, outgoingApiData);
    return mapGateSetApiToGateSet(incomingApiData);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const deleteGateById = createAsyncThunk<
  string,
  string,
  { state: RootState; rejectValue: ValidationResult[] | HttpError }
>('gates/delete', async (gateId, { getState, rejectWithValue }) => {
  const state = getState();
  const robotId = selectSelectedRobotId(state);

  if (robotId == null)
    return rejectWithValue({ code: '400', message: 'No robot id found', value: null });

  const roomId = state.localRobot.gates.data.find((g) => g.id === gateId)?.room;

  if (roomId == null)
    return rejectWithValue({ code: '400', message: 'No room id found', value: null });

  try {
    return await localRobotDataService.deleteRobotGateById(robotId, gateId);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getErrors = createAsyncThunk<
  PaginationWrapper<Error>,
  { robotId: string; page: number },
  { rejectValue: HttpError }
>('errors/get', async ({ robotId, page }, { rejectWithValue }) => {
  try {
    const incomingApiData = await localRobotDataService.getErrors({ robotId, page });
    return incomingApiData;
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const undoRedoLastAction =
  (undo: boolean): ThunkAction<void, RootState, unknown, UnknownAction> =>
  async (dispatch, getState) => {
    const state = getState();
    let lastAction: MapHistoryEntry | undefined = undefined;
    if (undo) {
      lastAction =
        state.localRobot.zonePlanner.history.undo[
          state.localRobot.zonePlanner.history.undo.length - 1
        ];
      trackEvent(AnalyticsEvent.ZonePlanChangeUndone, {
        history_action: lastAction != null ? MapHistoryAction[lastAction.action] : null,
        history_type: lastAction != null ? MapHistoryType[lastAction.type] : null,
      });
    } else {
      lastAction =
        state.localRobot.zonePlanner.history.redo[
          state.localRobot.zonePlanner.history.redo.length - 1
        ];
      trackEvent(AnalyticsEvent.ZonePlanChangeRedone, {
        history_action: lastAction != null ? MapHistoryAction[lastAction.action] : null,
        history_type: lastAction != null ? MapHistoryType[lastAction.type] : null,
      });
    }
    if (!lastAction) {
      return;
    }

    const updatedAction = { ...lastAction };

    switch (lastAction.type) {
      case MapHistoryType.CleaningZone: {
        const cleaningZone = lastAction.changedData;

        if (!isHistoryCleaningZone(cleaningZone)) return;

        const existingTRZone = selectRepeatPathByZoneId(state.localRobot, cleaningZone.id);
        const existingZone = selectZoneById(state.localRobot, cleaningZone.id);

        let TRZone: TeachAndRepeatZonePath | undefined = undefined;

        if (lastAction.additionalChangedData != null) {
          TRZone = cloneDeep(lastAction.additionalChangedData);
        }

        if (
          (undo && lastAction.action === MapHistoryAction.Created) ||
          (!undo && lastAction.action === MapHistoryAction.Deleted)
        ) {
          dispatch(deleteCleaningZoneById(cleaningZone.id));
        } else if (
          (undo && lastAction.action === MapHistoryAction.Deleted) ||
          (!undo && lastAction.action === MapHistoryAction.Created)
        ) {
          dispatch(
            postNewCleaningZone({
              cleaningZone,
              ...(TRZone != null && { teachAndRepeatPath: TRZone }),
            }),
          );
        } else {
          if (existingZone != null && isCleaningZone(existingZone)) {
            updatedAction.changedData = {
              id: cleaningZone.id,
              points: [...existingZone.points],
              startPosition:
                existingZone.startPosition != null ? { ...existingZone.startPosition } : null,
            };
            if (existingTRZone != null) {
              const trZoneSnapshot: TeachAndRepeatZonePath = {
                ...existingTRZone,
                path: existingTRZone.path.map((p) => ({ ...p })),
              };
              updatedAction.additionalChangedData = trZoneSnapshot;
            }
            // we do NOT need to adapt the starting point for tnr zones, because these can only be changed by dragging a path point, rather than the zone's starting position
          }
          dispatch(
            patchCleaningZone({
              id: cleaningZone.id,
              data: {
                cleaningZone: cleaningZone,
                ...(TRZone != null && { teachAndRepeatPath: TRZone }), // omit the teachAndRepeatPath if the TRZone is null
              },
            }),
          );
        }
        break;
      }

      case MapHistoryType.TeachAndRepeatZonePath: {
        const trPath = lastAction.changedData;

        if (!isHistoryTeachAndRepeatZonePath(trPath)) return;
        // This is just the patch case therefore there is no distinction here

        const existingTRPath = selectRepeatPathByZoneId(state.localRobot, trPath.zoneId);
        const existingZone = selectZoneById(state.localRobot, trPath.zoneId);

        if (existingTRPath != null) {
          updatedAction.changedData = {
            id: existingTRPath.id,
            zoneId: existingTRPath.zoneId,
            allowedDeviation: existingTRPath.allowedDeviation,
            resolveConfiguration: existingTRPath.resolveConfiguration,
            path: existingTRPath.path.map((p) => ({ ...p })),
          };
        }
        // if we update the first pose of the path we also need to update the start position of the corresponding cleaning zone
        const startPosition = {
          coordinateX: trPath.path[0].coordinateX,
          coordinateY: trPath.path[0].coordinateY,
          coordinateTheta: trPath.path[0].coordinateTheta,
          id: trPath.path[0].id,
          type: ZonePointType.CleaningZoneStartPosition,
        };

        dispatch(
          patchCleaningZone({
            id: trPath.zoneId,
            data: {
              cleaningZone: { startPosition },
              teachAndRepeatPath: trPath,
            },
          }),
        );
        break;
      }

      case MapHistoryType.RedZone: {
        const redZone = lastAction.changedData;
        if (!isHistoryRedZone(redZone)) return;

        if (
          (undo && lastAction.action === MapHistoryAction.Created) ||
          (!undo && lastAction.action === MapHistoryAction.Deleted)
        ) {
          dispatch(deleteRedZoneById(redZone.id));
        } else if (
          (undo && lastAction.action === MapHistoryAction.Deleted) ||
          (!undo && lastAction.action === MapHistoryAction.Created)
        ) {
          dispatch(postNewRedZone(redZone));
        } else {
          const existingZone = selectZoneById(state.localRobot, redZone.id);
          if (existingZone != null && !isCleaningZone(existingZone)) {
            updatedAction.changedData = {
              id: redZone.id,
              points: [...existingZone.points],
            };
          }
          dispatch(patchRedZone({ id: redZone.id, data: redZone }));
        }
        break;
      }
      case MapHistoryType.RuleZone: {
        const ruleZone = lastAction.changedData;
        if (!isHistoryRuleZone(ruleZone)) return;

        if (
          (undo && lastAction.action === MapHistoryAction.Created) ||
          (!undo && lastAction.action === MapHistoryAction.Deleted)
        ) {
          dispatch(deleteRuleZoneById(ruleZone.id));
        } else if (
          (undo && lastAction.action === MapHistoryAction.Deleted) ||
          (!undo && lastAction.action === MapHistoryAction.Created)
        ) {
          dispatch(postNewRuleZone(ruleZone));
        } else {
          const existingZone = selectZoneById(state.localRobot, ruleZone.id);
          if (existingZone != null && isRuleZone(existingZone)) {
            updatedAction.changedData = {
              id: ruleZone.id,
              points: [...existingZone.points],
            };
          }
          dispatch(patchRuleZone({ id: ruleZone.id, data: ruleZone }));
        }
        break;
      }
      case MapHistoryType.Gate:
        break;
      case MapHistoryType.Landmark: {
        const landmark = lastAction.changedData;
        if (!isHistoryLandmark(landmark)) return;

        if (
          (undo && lastAction.action === MapHistoryAction.Created) ||
          (!undo && lastAction.action === MapHistoryAction.Deleted)
        ) {
          dispatch(deleteLandmarkById(landmark.id));
        } else if (
          (undo && lastAction.action === MapHistoryAction.Deleted) ||
          (!undo && lastAction.action === MapHistoryAction.Created)
        ) {
          dispatch(postNewLandmark(landmark));
        } else {
          const existingLandmark = state.localRobot.landmarks.data.find(
            (l) => l.id === landmark.id,
          );
          if (existingLandmark != null) {
            updatedAction.changedData = {
              id: landmark.id,
              landmarkPoint: { ...existingLandmark.landmarkPoint },
            };
          }
          dispatch(patchLandmark({ id: landmark.id, data: landmark }));
        }
        break;
      }
      case MapHistoryType.WaypointSet: {
        const waypointSet = lastAction.changedData;
        if (!isHistoryWaypointSet(waypointSet) || state.localRobot.rooms.selectedId == null) return;
        updatedAction.changedData = {
          nodes: state.localRobot.waypoints.data.nodes.filter(
            (n) => n.room === state.localRobot.rooms.selectedId,
          ),
          edges: state.localRobot.waypoints.data.edges.filter(
            (n) => n.room === state.localRobot.rooms.selectedId,
          ),
        };

        dispatch(upsertWaypointSet({ room: state.localRobot.rooms.selectedId, ...waypointSet }));
        break;
      }
      default:
        break;
    }

    dispatch(popUndoRedoAction({ isUndoAction: undo, updatedAction }));
  };
